import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import "../../../../i18n";
import { Notice, ValidationRule } from "../../../../design/components";
import { ApiError } from "../../../../api/client";
import { useEntity } from "../../../../api/queries";
import type { ReadinessItem } from "../../../../api/types";
import { FAMILIES, familyLabel, type Family } from "../../../../domain/families";
import { goLive, notReadyItems, useReadiness } from "../onboardApi";
import styles from "../onboard.module.css";

const STATE_BY_STATUS = { ok: "pass", warn: "warn", blocked: "fail" } as const;

export const WHAT_HAPPENS_NEXT =
  "The hub will start collecting submitted documents from this company and sending them to the " +
  "accredited service provider, which validates them and reports them to the Oman Tax Authority.";

const SERVER_SAYS_NO = "The hub rechecked this company and it is not ready yet.";
export const WARN_NOTE = "Warnings do not stop go-live; blocked rows do.";

/** The readiness keys that belong to one document family (HUB-F06): `mapping:sales`, `prereq:rcm`… */
const FAMILY_ROW = /^(mapping|prereq|fields):(sales|selfbilled|rcm)$/;

/** Family-less rows first in server order, then one block per family in `FAMILIES` order.
    `credit_note_rule` has no family, so it stays in the first block. */
export function groupReadiness(items: ReadinessItem[]): {
  general: ReadinessItem[];
  families: { family: Family; items: ReadinessItem[] }[];
} {
  const general: ReadinessItem[] = [];
  const byFamily = new Map<Family, ReadinessItem[]>();
  for (const item of items) {
    const match = FAMILY_ROW.exec(item.key);
    if (!match) {
      general.push(item);
      continue;
    }
    const family = match[2] as Family;
    byFamily.set(family, [...(byFamily.get(family) ?? []), item]);
  }
  return {
    general,
    families: FAMILIES.map((spec) => spec.key)
      .filter((family) => byFamily.has(family))
      .map((family) => ({ family, items: byFamily.get(family)! })),
  };
}

function Rule({ item }: { item: ReadinessItem }) {
  return (
    <ValidationRule
      code={item.key}
      title={item.label}
      state={STATE_BY_STATUS[item.status] ?? "warn"}
      detail={item.detail}
    />
  );
}

export function StepGoLive({ entityId }: { entityId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const entity = useEntity(entityId);
  const readiness = useReadiness(entityId);
  const [override, setOverride] = useState<ReadinessItem[] | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [notReady, setNotReady] = useState(false);
  const [live, setLive] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // The server's 409 answer wins until the operator asks for a fresh check.
  const items = override ?? readiness.data?.items ?? [];
  // Only a blocked row holds the button back; a warn row (the credit-note rule, say) never does.
  const blocked = items.some((item) => item.status === "blocked");
  const warned = items.some((item) => item.status === "warn");
  const grouped = groupReadiness(items);
  const name = entity.data?.legalName ?? t("onboard.golive.thisCompany", "This company");

  async function confirm(): Promise<void> {
    setBusy(true);
    setError("");
    try {
      await goLive(entityId);
      setConfirming(false);
      setNotReady(false);
      setLive(true);
      await queryClient.invalidateQueries({ queryKey: ["entities", entityId] });
    } catch (exc) {
      setConfirming(false);
      const fresh = notReadyItems(exc);
      if (exc instanceof ApiError && exc.code === "NOT_READY" && fresh) {
        setOverride(fresh);
        setNotReady(true);
      } else {
        setError(
          exc instanceof ApiError
            ? exc.message
            : t("onboard.golive.error", "The company could not be made live."),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function checkAgain(): Promise<void> {
    setNotReady(false);
    setError("");
    setOverride(null);
    await readiness.refetch();
  }

  return (
    <section className={styles.panel}>
      <h2>{t("onboard.golive.title", "Go live")}</h2>
      <p className={styles.caption}>
        {t(
          "onboard.golive.caption",
          "Everything below has to hold before this company starts reporting. The hub checks the list again when you press the button.",
        )}
      </p>

      <div role="group" aria-label={t("onboard.golive.checklistLabel", "Go-live checklist")} className={styles.probe}>
        {readiness.isPending && !override ? (
          <p className={styles.empty}>{t("onboard.golive.readingReadiness", "Reading this company's readiness…")}</p>
        ) : null}
        {grouped.general.map((item) => (
          <Rule key={item.key} item={item} />
        ))}
        {grouped.families.map(({ family, items: rows }) => (
          <section key={family} aria-label={familyLabel(family)} className={styles.familyRows}>
            <h3>{familyLabel(family)}</h3>
            {rows.map((item) => (
              <Rule key={item.key} item={item} />
            ))}
          </section>
        ))}
      </div>

      {warned ? <Notice kind="warn">{t("onboard.golive.warnNote", WARN_NOTE)}</Notice> : null}
      {notReady ? <Notice kind="fail">{t("onboard.golive.serverSaysNo", SERVER_SAYS_NO)}</Notice> : null}
      {error ? <Notice kind="fail">{error}</Notice> : null}

      {live ? (
        <>
          <Notice kind="ok">{t("onboard.golive.isLive", "{{name}} is live.", { name })}</Notice>
          <div className={styles.foot}>
            <Link className="btn btn-primary" to={`/companies/${entityId}`}>
              {t("onboard.golive.open", "Open {{name}}", { name })}
            </Link>
          </div>
        </>
      ) : (
        <div className={styles.foot}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={blocked || busy || readiness.isPending}
            onClick={() => setConfirming(true)}
          >
            {t("onboard.golive.goLive", "Go live")}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || readiness.isFetching}
            onClick={() => void checkAgain()}
          >
            {t("onboard.golive.checkAgain", "Check again")}
          </button>
        </div>
      )}

      {confirming ? (
        <div className={styles.confirm} role="dialog" aria-label={t("onboard.golive.goLive", "Go live")}>
          <p>{t("onboard.golive.whatHappensNext", WHAT_HAPPENS_NEXT)}</p>
          <div className={styles.foot}>
            <button type="button" className="btn" onClick={() => setConfirming(false)}>
              {t("onboard.golive.cancel", "Cancel")}
            </button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void confirm()}>
              {t("onboard.golive.confirm", "Confirm")}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
